import { Component, computed, effect, inject, input, OnChanges, OnDestroy, OnInit, signal } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { forkJoin, Subscription } from 'rxjs';
import { PlanName } from '../../../components/helpers';
import { defaultTableDefOptions } from '../../../helpers';
import { CustomerPortalService } from '../../../service/customer-portal.service';
import { LocationService } from '../../../service/location.service';
import { NotifyService } from '../../../service/notify.service';
import { PosthogService } from '../../../service/posthog.service';
import { PresenceResponse, SignalrPresenceService } from '../../../service/signalr-presence.service';
import { SubscriptionService } from '../../../service/subscription.service';
import { Branch, FeatureFlag, LocationPlanMap, LocationWithPlan, SelfServeTheftPlanPayload, StripePlan } from '../../../types';
import { CellBillingPortalCellActionsComponent } from './billing-portal-cell-actions/billing-portal-cell-actions.component';
import { CellBillingPortalCellAttributesComponent } from './billing-portal-cell-attributes/billing-portal-cell-attributes.component';
import { CellBillingPortalCellPlanComponent } from './billing-portal-cell-plan/billing-portal-cell-plan.component';
import { CellBillingPortalCellProfileComponent } from './billing-portal-cell-profile/billing-portal-cell-profile.component';

@Component({
  selector: 'app-billing-portal-branch-card',
  templateUrl: './billing-portal-branch-card.component.html',
  styleUrls: ['./billing-portal-branch-card.component.scss'],
  standalone: false,
})
export class BillingPortalBranchCardComponent implements OnInit, OnChanges, OnDestroy {
  private locationService = inject(LocationService);
  private customerPortalService = inject(CustomerPortalService);
  private subscriptionService = inject(SubscriptionService);
  private presenceService = inject(SignalrPresenceService);
  private posthogService = inject(PosthogService);
  private notifyService = inject(NotifyService);

  public presenceData = signal<PresenceResponse>([null, []]);
  private presenceSubscription?: Subscription;

  public selfServePlanPrices = signal<string[]>([]);

  public branchId = input.required<number>();
  public branchData = input<Branch>();

  public stripePlans = signal<StripePlan[]>([]);
  public branchLocations = signal<LocationWithPlan[]>([]);
  public isLoading = signal<boolean>(false);
  public isEditingPlans = signal<boolean>(false);
  public isSavingPlans = signal<boolean>(false);

  public selfServePrices = computed(() => this.stripePlans().filter((p) => this.selfServePlanPrices().includes(p.id)));
  public selfServeBranches = signal<number[]>([]);

  public showViewBilling = signal<boolean>(false);
  public showEditPlans = computed(() => {
    const branch = this.branchData();
    return !!branch && this.selfServePrices().length > 0 && this.selfServeBranches().includes(branch.id);
  });
  public isAccessSuspended = computed(() => this.branchData()?.keystore?.suspend_access ?? false);

  private newLocationPlanNames: Record<string, string> = {};
  public editLocationPlanMap = signal<LocationPlanMap>({});
  public readonly allPlanNamesByPriceId = signal<Record<string, string>>({});

  public numCompleteLocations = computed(() => this.branchLocations().filter((loc) => loc.profile?.status === 'Completed').length);

  public readonly tableDef: GridOptions = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly columnDefs: ColDef<LocationWithPlan>[] = [
    {
      field: 'name',
      headerName: 'Location Name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Loss Prevention Plan',
      cellRenderer: CellBillingPortalCellPlanComponent,
      sortable: false,
      cellRendererParams: {
        branchId: () => this.branchId(),
        isEditing: () => this.isEditingPlans(),
        editData: () => this.editLocationPlanMap(),
        allPlanNamesByPriceId: () => this.allPlanNamesByPriceId(),
        selfServePrices: () => this.selfServePrices(),
        updatePlan: (location: LocationWithPlan, newPlan: StripePlan | undefined) => this.updateCurrentPlanData(location, newPlan),
      },
    },
    {
      headerName: 'Attributes',
      cellRenderer: CellBillingPortalCellAttributesComponent,
      sortable: false,
    },
    {
      headerName: 'Profile',
      cellRenderer: CellBillingPortalCellProfileComponent,
      cellRendererParams: {
        branchId: () => this.branchId(),
      },
      sortable: false,
    },
    {
      headerName: '',
      suppressKeyboardEvent: () => true,
      cellRenderer: CellBillingPortalCellActionsComponent,
      cellRendererParams: {
        branchId: () => this.branchId(),
      },
      sortable: false,
    },
  ];

  constructor() {
    effect(() => {
      const isReady = this.posthogService.isReady();
      if (!isReady) return;

      const viewBillingFlag = this.posthogService.getFeatureFlagPayloadIfEnabled<number[]>('viewBillingFeature', []);

      this.showViewBilling.set(viewBillingFlag.enabled && viewBillingFlag.payload?.includes(this.branchId()));

      const selfServeFlag = this.posthogService.getFeatureFlagPayloadIfEnabled<SelfServeTheftPlanPayload>('self_serve_theft_plan', {});

      if (selfServeFlag.enabled) {
        const { theft_plan_prices, branches } = selfServeFlag.payload;

        this.selfServePlanPrices.set(theft_plan_prices);
        this.selfServeBranches.set(branches);
      }
    });
  }

  ngOnInit() {
    this.presenceService.connectPresencesHub().subscribe(() => {});
  }

  ngOnChanges() {
    this.isLoading.set(true);

    const branch = this.branchData();
    if (!branch) return;
    if (this.isAccessSuspended()) {
      this.branchLocations.set([]);
      this.stripePlans.set([]);
      this.isLoading.set(false);
      return;
    }

    forkJoin({
      locations: this.locationService.getAllLocations({
        branch_ids: [this.branchId()],
        include_profile: true,
      }),
      prices: this.subscriptionService.getTheftPlans(branch.customer_type),
    }).subscribe(({ locations, prices }) => {
      const locationsWithPlans: LocationWithPlan[] = locations.items.map((loc) => {
        const price = prices.find((p) => p.id === loc.stripe_price_id);

        return {
          ...loc,
          _planName: (price?.product.name.toLowerCase() ?? 'noplan') as PlanName,
        };
      });

      const priceNameMap = prices.reduce((prev, cur) => {
        prev[cur.id] = cur.product.name;
        return prev;
      }, {});

      this.allPlanNamesByPriceId.set(priceNameMap);

      this.stripePlans.set(prices);
      this.branchLocations.set(locationsWithPlans);
      this.isLoading.set(false);
    });
  }

  ngOnDestroy() {
    this.cancelPlanEdit();
  }

  public manageBilling() {
    const branch = this.branchData();
    if (!branch?.stripe_customer_id) return;

    const useFlywire = this.posthogService.isReady() && this.posthogService.isFeatureEnabledByEnum(FeatureFlag.EnableNetsuiteMigration);

    const session$ = useFlywire
      ? this.customerPortalService.createFlywireSession(branch.id)
      : this.customerPortalService.createSession(branch.stripe_customer_id);

    session$.subscribe({
      next: (response: { url: string }) => {
        window.open(response.url, '_blank');
      },
    });
  }

  public editPlan() {
    const branch = this.branchData();
    if (!branch) return;

    this.presenceSubscription = this.presenceService.joinRoom(`SUBSCRIPTION_EDIT_${branch.id}`).subscribe({
      next: (data: PresenceResponse) => {
        this.presenceData.set(data);
      },
    });

    this.isEditingPlans.set(true);

    const planMap = {};

    this.branchLocations().forEach((location) => {
      planMap[location.id] = {
        current: {
          price_id: location.stripe_price_id,
        },
        previous: {
          price_id: location.stripe_price_id,
        },
      };
    });

    this.editLocationPlanMap.set(planMap);
  }

  public cancelPlanEdit() {
    this.presenceSubscription?.unsubscribe();
    this.isEditingPlans.set(false);
    this.newLocationPlanNames = {};
  }

  public savePlans() {
    if (this.isSavingPlans()) return;

    this.isSavingPlans.set(true);
    this.cancelPlanEdit();

    const branch = this.branchData();
    if (!branch) {
      this.isSavingPlans.set(false);
      return;
    }
    const planMap = this.editLocationPlanMap();

    this.posthogService.capture('update_theft_plan', {
      admin: false,
      branchId: branch.id,
      planMap,
    });

    this.subscriptionService
      .updateSubscription({
        branch_id: branch.id,
        location_plans: Object.keys(planMap).map((key) => ({
          location_id: parseInt(key),
          location_plan: planMap[key].current,
        })),
        previous_plans: Object.keys(planMap).map((key) => ({
          location_id: parseInt(key),
          location_plan: planMap[key].previous,
        })),
      })
      .subscribe({
        next: () => {
          this.branchLocations.update((locations) => {
            locations.forEach((location) => {
              if (planMap[location.id]) {
                location.stripe_price_id = planMap[location.id].current.price_id;
              }

              if (this.newLocationPlanNames[location.id]) {
                location._planName = this.newLocationPlanNames[location.id] as PlanName;
              }
            });

            return structuredClone(locations);
          });

          this.cancelPlanEdit();
          this.isSavingPlans.set(false);
          this.presenceSubscription?.unsubscribe();
        },
        error: (error) => {
          if (error.error?.error.includes('Please refresh')) {
            this.notifyService.notify(error.error.error, 'Dismiss');
          } else if (error.error?.title) {
            this.notifyService.notify(error.error.title, 'Dismiss');
          } else {
            this.notifyService.notify('Error saving subscription plan. Please contact support', 'Dismiss');
          }

          this.isSavingPlans.set(false);
        },
      });
  }

  public updateCurrentPlanData(location: LocationWithPlan, newStripePlan: StripePlan | undefined) {
    this.newLocationPlanNames[location.id] = newStripePlan?.product.name ?? 'noplan';
    this.editLocationPlanMap.update((v) => {
      const newPlans = structuredClone(v);
      newPlans[location.id].current.price_id = newStripePlan?.id ?? undefined;
      return newPlans;
    });
  }
}
